// Bottle Sort (water-sort) - pure game logic + solver + level generator (no DOM).
//
// Rules: bottles hold up to BOTTLE_SIZE units of colored liquid. You may pour
// the top run of one bottle onto another if the target is empty or its top
// color matches (as many units move as fit). A bottle filled with BOTTLE_SIZE
// units of ONE color is "served" and removed from play; emptied bottles stay
// as workspace. Serve every color to win.
//
// Solvability guarantee: a level is only accepted by the generator when the
// DFS solver PROVES it solvable; the solver's solution is replayed move by
// move as an independent check (see scripts/validate-watersort.ts), and its
// length becomes the level's par.

export const BOTTLE_SIZE = 5;

export type Bottle = number[]; // color indices, bottom -> top
export interface Slot {
  id: number;
  colors: Bottle;
}
export type State = Slot[]; // bottles still in play (served bottles removed)

export interface WsLevel {
  colors: number;
  /** Initial bottle contents; trailing empty bottles are the workspace. */
  bottles: Bottle[];
  par: number; // solver solution length
}

export interface WsMove {
  from: number; // slot id
  to: number; // slot id
}

export function cloneState(state: State): State {
  return state.map((s) => ({ id: s.id, colors: s.colors.slice() }));
}

export function topRun(b: Bottle): { color: number; count: number } | null {
  if (b.length === 0) return null;
  const color = b[b.length - 1];
  let count = 1;
  for (let k = b.length - 2; k >= 0 && b[k] === color; k--) count++;
  return { color, count };
}

export function isUniform(b: Bottle): boolean {
  return b.length > 0 && b.every((c) => c === b[0]);
}

export function canPour(state: State, i: number, j: number): boolean {
  if (i === j || !state[i] || !state[j]) return false;
  const a = state[i].colors;
  const b = state[j].colors;
  if (a.length === 0 || b.length >= BOTTLE_SIZE) return false;
  return b.length === 0 || b[b.length - 1] === a[a.length - 1];
}

export interface PourResult {
  state: State;
  moved: number;
  /** Set when the pour completed a bottle (full + one color). The bottle is
   * still IN the returned state so the UI can animate it; call removeServed. */
  servedId: number | null;
}

export function pour(state: State, i: number, j: number): PourResult | null {
  if (!canPour(state, i, j)) return null;
  const next = cloneState(state);
  const a = next[i].colors;
  const b = next[j].colors;
  const run = topRun(a)!;
  const moved = Math.min(run.count, BOTTLE_SIZE - b.length);
  for (let k = 0; k < moved; k++) b.push(a.pop()!);
  const servedId = b.length === BOTTLE_SIZE && isUniform(b) ? next[j].id : null;
  return { state: next, moved, servedId };
}

export function removeServed(state: State, id: number): State {
  return state.filter((s) => s.id !== id);
}

export function isSolved(state: State): boolean {
  return state.every((s) => s.colors.length === 0);
}

/**
 * Moves that can make progress. Pointless pours are skipped: a uniform bottle
 * into an empty one (same position, relabeled), and all empty bottles are
 * interchangeable so only the first is offered as a target. If this returns []
 * and the level is not solved, the player is genuinely stuck.
 */
export function meaningfulMoves(state: State): WsMove[] {
  const out: Array<WsMove & { score: number }> = [];
  let firstEmpty = -1;
  for (let j = 0; j < state.length; j++) {
    if (state[j].colors.length === 0) {
      firstEmpty = j;
      break;
    }
  }
  for (let i = 0; i < state.length; i++) {
    const a = state[i].colors;
    const run = topRun(a);
    if (!run) continue;
    const uniform = isUniform(a);
    for (let j = 0; j < state.length; j++) {
      if (i === j) continue;
      const b = state[j].colors;
      if (b.length >= BOTTLE_SIZE) continue;
      if (b.length === 0) {
        if (j !== firstEmpty || uniform) continue;
        out.push({ from: state[i].id, to: state[j].id, score: 0 });
      } else if (b[b.length - 1] === run.color) {
        const space = BOTTLE_SIZE - b.length;
        const completes = isUniform(b) && b.length + Math.min(run.count, space) === BOTTLE_SIZE;
        const fits = run.count <= space;
        out.push({ from: state[i].id, to: state[j].id, score: completes ? 3 : fits ? 2 : 1 });
      }
    }
  }
  // Try promising moves first: completing a bottle, then whole-run pours.
  return out.sort((x, y) => y.score - x.score).map(({ from, to }) => ({ from, to }));
}

function stateKey(state: State): string {
  // Bottles are interchangeable, so a sorted key collapses permutations.
  return state
    .map((s) => s.colors.join(","))
    .sort()
    .join("|");
}

/**
 * Depth-first search with memoization for a solution from `state`. Returns the
 * move list (slot ids) or null if none found within the node budget. Not
 * guaranteed shortest, but the move ordering keeps it close - good enough for
 * a fair par.
 */
export function solveFrom(state: State, maxNodes = 250000): WsMove[] | null {
  const visited = new Set<string>();
  let budget = maxNodes;
  const path: WsMove[] = [];

  function dfs(st: State): boolean {
    if (isSolved(st)) return true;
    if (--budget < 0) return false;
    const k = stateKey(st);
    if (visited.has(k)) return false;
    visited.add(k);
    for (const mv of meaningfulMoves(st)) {
      const i = st.findIndex((s) => s.id === mv.from);
      const j = st.findIndex((s) => s.id === mv.to);
      const res = pour(st, i, j);
      if (!res) continue;
      const ns = res.servedId != null ? removeServed(res.state, res.servedId) : res.state;
      path.push(mv);
      if (dfs(ns)) return true;
      path.pop();
    }
    return false;
  }

  return dfs(cloneState(state)) ? path.slice() : null;
}

// Deterministic RNG (mulberry32).
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface WsOptions {
  colors: number;
  empties: number;
  minMoves: number; // difficulty gate: solver solution must be at least this long
  seed: number;
}

export function slotsFor(bottles: Bottle[]): State {
  return bottles.map((colors, i) => ({ id: i, colors: colors.slice() }));
}

/**
 * Generate one PROVABLY solvable level: shuffle colors*BOTTLE_SIZE units over
 * the color bottles, then accept only if the solver finds a solution of at
 * least minMoves. Falls back to the hardest solvable candidate seen; if not a
 * single candidate is solvable (practically impossible for sane parameters),
 * an extra empty bottle is added - strictly easier - and the search repeats,
 * so this always terminates with a verified level.
 */
export function generateWsLevel(o: WsOptions): WsLevel {
  let best: WsLevel | null = null;

  for (let attempt = 0; attempt < 60; attempt++) {
    const rng = makeRng(o.seed + attempt * 7919);
    const flat: number[] = [];
    for (let c = 0; c < o.colors; c++) for (let k = 0; k < BOTTLE_SIZE; k++) flat.push(c);
    // Fisher-Yates shuffle.
    for (let i = flat.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [flat[i], flat[j]] = [flat[j], flat[i]];
    }
    const bottles: Bottle[] = [];
    for (let c = 0; c < o.colors; c++) bottles.push(flat.slice(c * BOTTLE_SIZE, (c + 1) * BOTTLE_SIZE));
    // A bottle that starts uniform would be served before the first move.
    if (bottles.some((b) => isUniform(b))) continue;
    for (let e = 0; e < o.empties; e++) bottles.push([]);

    const sol = solveFrom(slotsFor(bottles));
    if (!sol) continue;
    const lvl: WsLevel = { colors: o.colors, bottles, par: sol.length };
    if (sol.length >= o.minMoves) return lvl;
    if (!best || lvl.par > best.par) best = lvl;
  }

  if (best) return best;
  return generateWsLevel({ ...o, empties: o.empties + 1, seed: o.seed + 31337 });
}

/**
 * Endless difficulty curve: more colors (up to 9), rising minimum solution
 * length. Bosses (every 10th) demand a longer solve; the level after a boss
 * drops one color as a breather. Deterministic per level number.
 */
export function optionsForWsLevel(n: number): WsOptions {
  const isBoss = n >= 10 && n % 10 === 0;
  const isBreather = n > 10 && n % 10 === 1;
  let colors = Math.min(9, 3 + Math.floor(n / 4));
  if (isBreather) colors = Math.max(3, colors - 1);
  const minMoves = colors * 3 + Math.min(10, Math.floor(n / 3)) + (isBoss ? 4 : 0);
  return { colors, empties: 2, minMoves, seed: 5000 + n * 173 };
}
